/**
 * Footer Component
 * 
 * Simple footer with:
 * - Legal links
 * - Copyright notice
 * - Trust indicators
 * 
 * Positioned at bottom of funnel pages.
 */

import React from 'react';

// ============================================
// TYPES
// ============================================

interface FooterProps {
  /** Show privacy/legal links */
  showLinks?: boolean;
  /** Custom copyright text */
  copyrightText?: string;
}

// ============================================
// COMPONENT
// ============================================

/**
 * Footer - Minimal footer for trust and legal compliance
 */
export const Footer: React.FC<FooterProps> = ({
  showLinks = true,
  copyrightText,
}) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full py-6 px-4">
      <div className="max-w-question mx-auto">
        {/* Trust indicators */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <TrustBadge icon="🔒" text="Secure" />
          <TrustBadge icon="🛡️" text="Private" />
          <TrustBadge icon="✓" text="No Spam" />
        </div>

        {/* Legal links */}
        {showLinks && (
          <div className="flex items-center justify-center gap-4 mb-3">
            <FooterLink href="/privacy">Privacy Policy</FooterLink>
            <span className="text-border">•</span>
            <FooterLink href="/terms">Terms of Service</FooterLink>
          </div>
        )}

        {/* Copyright */}
        <p className="text-center text-xs text-text-secondary/60">
          {copyrightText || `© ${currentYear} AI Automation Solutions. All rights reserved.`}
        </p>
      </div>
    </footer>
  );
};

Footer.displayName = 'Footer';

// ============================================
// SUBCOMPONENTS
// ============================================

interface TrustBadgeProps {
  icon: string;
  text: string;
}

/**
 * TrustBadge - Small trust indicator
 */
const TrustBadge: React.FC<TrustBadgeProps> = ({ icon, text }) => {
  return (
    <div className="flex items-center gap-1 text-xs text-text-secondary">
      <span aria-hidden="true">{icon}</span>
      <span>{text}</span>
    </div>
  );
};

interface FooterLinkProps {
  href: string;
  children: React.ReactNode;
}

/**
 * FooterLink - Styled footer link
 */
const FooterLink: React.FC<FooterLinkProps> = ({ href, children }) => {
  return (
    <a
      href={href}
      className="text-xs text-text-secondary hover:text-primary transition-colors"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};

// ============================================
// COMPACT FOOTER VARIANT
// ============================================

/**
 * CompactFooter - Even smaller footer for tight spaces
 */
export const CompactFooter: React.FC = () => {
  return (
    <div className="text-center py-4">
      <p className="text-xs text-white/80">
        Your information is secure and will never be shared.
      </p>
    </div>
  );
};

CompactFooter.displayName = 'CompactFooter';

export default Footer;
